import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { Button } from "@/components/ui/button";
import { Download, FileText, Image as ImageIcon, File } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { DiskFile } from "@/types/disk";

// Галерея просмотра
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

export default function PublicDiskView() {
  const { folderId } = useParams<{ folderId: string }>();
  const [files, setFiles] = useState<DiskFile[]>([]);
  const [folderName, setFolderName] = useState<string>("Публичная папка");
  const [folderType, setFolderType] = useState<string>(""); // Новое — тип папки
  const [isFolderPublic, setIsFolderPublic] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const [openGallery, setOpenGallery] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const loadData = async () => {
      if (!folderId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const folderDocRef = doc(db, "diskFolders", folderId);
        const folderSnap = await getDoc(folderDocRef);

        if (!folderSnap.exists()) {
          setFolderName("Папка не найдена");
          setIsFolderPublic(false);
          setLoading(false);
          return;
        }

        const folderData = folderSnap.data();
        const name = folderData?.name || "Публичная папка";
        const isPublic = folderData?.isPublic === true;
        const type = folderData?.type || "default"; // ← берём тип папки

        setFolderName(folderData.name);
        setFolderType(type);
        setIsFolderPublic(isPublic);

        if (!isPublic) {
          setLoading(false);
          return;
        }

        const filesQuery = query(
          collection(db, "diskFiles"),
          where("userId", "==", "4"),
          where("path", "==", folderData.path)
        );

        const snapshot = await getDocs(filesQuery);

        const filePromises = snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data() as DiskFile;
          try {
            const storagePath = `disk/4/${folderData.path}/${data.name}`;
            const fileRef = ref(storage, storagePath);
            const url = await getDownloadURL(fileRef);
            return { ...data, url };
          } catch (err) {
            console.error("Ошибка файла:", data.name, err);
            return null;
          }
        });

        const fileList = (await Promise.all(filePromises)).filter(Boolean) as DiskFile[];
        setFiles(fileList);
      } catch (err) {
        console.error("Общая ошибка:", err);
        toast({ title: "Ошибка загрузки", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [folderId]);

  const handleOpenGallery = (index: number) => {
    setSelectedIndex(index);
    setZoom(1);
    setOpenGallery(true);
  };

  const handleZoom = () => {
    setZoom((prev) => (prev === 1 ? 2 : 1));
  };

  const handleDownload = async (url: string, name: string) => {
    toast({ title: "Файл скачивается..." });
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast({ title: "Ошибка скачивания", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <p className="text-xl animate-pulse">Загрузка...</p>
      </div>
    );
  }

  if (isFolderPublic === false) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4 text-red-400">Доступ ограничен</h2>
          <p className="text-lg mb-6">
            Эта папка не публичная.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white p-6 md:p-10">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-center bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          {folderName}
        </h1>

        {files.length === 0 ? (
          <div className="text-center py-20 bg-purple-950/30 rounded-2xl border border-purple-700/50 max-w-lg mx-auto">
            <p className="text-xl text-purple-300 mb-4">Нет файлов в этой папке</p>
            <p className="text-sm text-purple-400">
              Добавьте фото/видео через приложение.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {files.map((file, index) => (
              <div
                key={file.id}
                className="bg-purple-950/40 border border-purple-700/50 rounded-xl overflow-hidden hover:border-purple-500 transition-all group shadow-lg shadow-purple-900/20 cursor-pointer"
                onClick={() => handleOpenGallery(index)}
              >
                <div className="aspect-square flex items-center justify-center bg-black/30 p-4">
                  {file.type?.startsWith("image/") ? (
                    <img
                      src={file.url}
                      alt={file.name}
                      className="max-h-full max-w-full object-contain rounded"
                      loading="lazy"
                    />
                  ) : file.type?.startsWith("video/") ? (
                    <video
                      src={file.url}
                      className="max-h-full max-w-full object-contain rounded opacity-80 group-hover:opacity-100"
                      muted
                      playsInline
                    />
                  ) : file.type?.includes("pdf") || file.type?.includes("doc") ? (
                    <FileText className="h-20 w-20 text-blue-400" />
                  ) : (
                    <File className="h-20 w-20 text-gray-400" />
                  )}
                </div>

                <div className="p-3">
                  <p className="text-sm font-medium truncate mb-1">{file.name}</p>
                  <p className="text-xs text-gray-400 truncate">{file.description || "Без описания"}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-purple-300 hover:text-purple-100 hover:bg-purple-800/50 mt-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(file.url, file.name);
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Скачать
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Галерея */}
      <Dialog open={openGallery} onOpenChange={setOpenGallery}>
        <DialogContent className="max-w-5xl h-[90vh] bg-black/90 p-0 border-none">
          <DialogHeader className="absolute top-4 left-4 z-10">
            <DialogTitle className="text-white text-xl">
              {files[selectedIndex]?.name || "Просмотр"}
            </DialogTitle>
            <p className="text-sm text-gray-300 mt-1">{files[selectedIndex]?.description || "Без описания"}</p>
          </DialogHeader>

          <Carousel className="w-full h-full">
            <CarouselContent>
              {files.map((file, index) => (
                <CarouselItem key={index}>
                  <div className="flex items-center justify-center h-[85vh] p-4">
                    {file.type?.startsWith("image/") ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        className="max-h-full max-w-full object-contain cursor-zoom-in transition-transform duration-300"
                        style={{ transform: `scale(${zoom})` }}
                        onClick={handleZoom}
                      />
                    ) : file.type?.startsWith("video/") ? (
                      <video
                        src={file.url}
                        controls
                        autoPlay
                        className="max-h-full max-w-full object-contain rounded"
                      />
                    ) : (
                      <div className="text-white text-center">
                        <p>Формат не поддерживается для просмотра</p>
                        <Button onClick={() => handleDownload(file.url, file.name)}>
                          Скачать
                        </Button>
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="absolute left-4" />
            <CarouselNext className="absolute right-4" />
          </Carousel>

          <Button
            variant="ghost"
            className="absolute top-4 right-4 text-white"
            onClick={() => setOpenGallery(false)}
          >
            Закрыть
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}